import React, { useState, useEffect, useRef } from 'react';
import './TrendAnalysisPage.css';
import { getVehicleTypes, getLocations } from '../../api/apiClient';
import tooltipTexts from '../../utils/tooltipTexts';

const TrendAnalysisPage = () => {
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [violationData, setViolationData] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vehicleError, setVehicleError] = useState(null);
  const [maxAttemptNumber, setMaxAttemptNumber] = useState(0);
  
  // Create a ref for the table container
  const tableContainerRef = useRef(null);

  // Fetch locations and set admin's location on mount
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user?.locationId) {
      setSelectedLocation(user.locationId);
      fetchLocations(user.locationId);
      fetchVehicleTypes(user.locationId);
    } else {
      console.error('No location ID found for admin');
    }
  }, []);

  // Fetch vehicle types when location changes
  useEffect(() => {
    if (selectedLocation) {
      setSelectedEquipment('');
      setVehicleError(null);
      setVehicleTypes([]);
      fetchVehicleTypes(selectedLocation);
    } else {
      setVehicleTypes([]);
      setVehicleError(null);
    }
  }, [selectedLocation]);

  // Update session dropdown when equipment changes
  useEffect(() => {
    if (selectedEquipment) {
      // Reset session selection when vehicle changes
      setSelectedSession('');
      setMaxAttemptNumber(0);
      
      // Fetch session data to determine highest attempt number
      setIsLoading(true);
      const fetchSessionData = async () => {
        try {
          const response = await fetch(`http://eframeehs.in:3001/api/vehicle-max-session?vehicleId=${selectedEquipment}`);
          if (!response.ok) {
            throw new Error('Failed to fetch session data');
          }
          
          const data = await response.json();
          if (data && data.maxSession) {
            setMaxAttemptNumber(data.maxSession);
          } else {
            setMaxAttemptNumber(0);
          }
        } catch (err) {
          console.error('Error fetching session data:', err);
          setMaxAttemptNumber(0);
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchSessionData();
    }
  }, [selectedEquipment]);

  const fetchLocations = async (adminLocationId) => {
    try {
      const locations = await getLocations(adminLocationId);
      console.log('Locations fetched:', locations);
      setLocations(locations);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchVehicleTypes = async (locationId) => {
    setIsLoadingVehicles(true);
    setVehicleError(null);
    setVehicleTypes([]);
    
    try {
      console.log(`Fetching vehicles for location ID: ${locationId}`);
      const data = await getVehicleTypes(locationId);
      
      console.log(`Received vehicles for location ${locationId}:`, data);
      
      if (data.length === 0) {
        // Set error message when no vehicles are registered for this location
        const selectedLocationName = locations.find(loc => 
          String(loc.LocationID || loc.locationID || loc.locationId || loc.id) === String(locationId)
        )?.LocationName || 'Selected location';
        
        setVehicleError(`${selectedLocationName} has no registered vehicles`);
      } else {
        setVehicleTypes(data);
      }
    } catch (err) {
      console.error('Error fetching vehicle types:', err);
      setVehicleError('Failed to load vehicles for this location');
    } finally {
      setIsLoadingVehicles(false);
    }
  };

  // Generate session options based on highest attempt number
  const generateSessionOptions = () => {
    const options = [];
    
    for (let i = 1; i <= maxAttemptNumber; i++) {
      options.push(
        <option key={i} value={i.toString()}>Session {i}</option>
      );
    }
    
    if (options.length === 0) {
      options.push(<option key="none" value="">No sessions available</option>);
    }
    
    return options;
  };

  const fetchViolationData = async () => {
    if (!selectedSession || !selectedEquipment) {
      setError('Please select both Equipment and Session');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const requestBody = {
        AttemptNumber: selectedSession,
        vehicleId: selectedEquipment
      };

      // Add locationId to request if one is selected
      if (selectedLocation) {
        requestBody.locationId = selectedLocation;
      }

      // Use the new endpoint that includes descriptions
      const response = await fetch('http://eframeehs.in:3001/api/violation-percentage-by-date-with-descriptions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch violation data');
      }

      const data = await response.json();
      setViolationData(data);

      // Scroll to the table container after data loads with a slight delay
      // to ensure the component has re-rendered
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 100);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load violation data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchViolationData();
  };

  const handleExportExcel = () => {
    // Create a CSV string from the violation data
    if (violationData.length === 0) {
      alert('No data to export');
      return;
    }
    
    // Get the selected vehicle name if available
    let selectedVehicleName = 'All';
    
    if (selectedEquipment && selectedEquipment !== '') {
      // Find the vehicle by ID
      const foundVehicle = vehicleTypes.find(v => String(v.VehicleID) === String(selectedEquipment));
      
      if (foundVehicle) {
        // Use ScreenName for display, fallback to VehicleName
        selectedVehicleName = foundVehicle.ScreenName || foundVehicle.VehicleName || 'Unknown';
      }
    }

    const violationDataWithRank = violationData.map((item, index) => {
      return {
        ...item,
        Rank: index + 1
      };
    });

    const headers = ['Rank', 'Violation', 'Percentage (%)'];
    const csvContent = [
      headers.join(','),
      ...violationDataWithRank.map(item => 
        `${item.Rank},"${item.ViolationScreenName}",${parseFloat(item.ViolationPercentage).toFixed(2)}`
      )
    ].join('\n');

    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Create file name with vehicle name and session
    const sessionSuffix = selectedSession ? ` - Session ${selectedSession}` : '';
    const fileName = `${selectedVehicleName} Trend Analysis${sessionSuffix}`;
    link.setAttribute('download', fileName + '.csv');
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get vehicle display name by ID
  const getVehicleDisplayName = (vehicleId) => {
    if (!vehicleId) return 'Unknown Vehicle';
    
    const vehicle = vehicleTypes.find(v => v && String(v.VehicleID) === String(vehicleId));
    return vehicle ? (vehicle.ScreenName || vehicle.VehicleName || `Vehicle ${vehicleId}`) : `Vehicle ${vehicleId}`;
  };

  // Debug current state
  console.log('Current state:', {
    selectedLocation,
    selectedEquipment,
    selectedSession,
    vehicleTypes: vehicleTypes.length,
    vehicleError,
    isLoadingVehicles
  });

  return (
    <div className="trend-analysis-page">
      <div className="content-area">
        <div className="filter-section">
          <div className="dropdown-container">
            <div className="select-wrapper">
              <select 
                value={selectedLocation} 
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="location-select"
                disabled={true} // Location is now disabled as it's fixed to admin's location
              >
                {locations.map(location => (
                  <option key={location.LocationID || location.id} value={location.LocationID || location.id}>
                    {location.LocationName || location.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="select-wrapper">
              <select 
                value={selectedEquipment} 
                onChange={(e) => setSelectedEquipment(e.target.value)}
                className="equipment-select"
                disabled={isLoadingVehicles || !selectedLocation || vehicleError !== null}
              >
                {isLoadingVehicles ? (
                  <option value="">Loading equipment types...</option>
                ) : !selectedLocation ? (
                  <option value="">Select Location First</option>
                ) : vehicleError ? (
                  <option value="">{vehicleError}</option>
                ) : vehicleTypes.length === 0 ? (
                  <option value="">No equipment types available</option>
                ) : (
                  <>
                    <option value="">Select Mobile Equipment Type*</option>
                    {vehicleTypes.map(vehicle => (
                      <option key={vehicle.VehicleID} value={vehicle.VehicleID}>
                        {vehicle.ScreenName || vehicle.VehicleName || `Vehicle ${vehicle.VehicleID}`}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
            
            <div className="select-wrapper">
              <select
                value={selectedSession}
                onChange={(e) => setSelectedSession(e.target.value)}
                className="session-select"
                disabled={isLoading || !selectedEquipment || maxAttemptNumber === 0}
              >
                <option value="">Select Session*</option>
                {generateSessionOptions()}
              </select>
            </div>
            
            <button 
              className="export-button"
              onClick={handleExportExcel}
              disabled={violationData.length === 0}
            >
              <span>Export Excel Sheet</span>
              <span className="export-icon">↓</span>
            </button>
          </div>
          
          <button 
            className="submit-button" 
            onClick={handleSubmit}
            disabled={isLoading || !selectedLocation || !selectedEquipment || !selectedSession || (vehicleError !== null)}
          >
            {isLoading ? 'Loading...' : 'Submit'}
          </button>
        </div>
        
        <div className="violation-table-container" ref={tableContainerRef}>
          <h2 className="table-title">
            {selectedEquipment ? (() => {
              const vehicleName = getVehicleDisplayName(selectedEquipment);
              return `${vehicleName} Violation Analysis`;
            })() : (
              <>
                Violation Trend Analysis
                <span className="header-tooltip">
                  <span className="info-icon">i</span>
                  <span className="tooltip-text">{tooltipTexts.violationTrendAnalysisHeader}</span>
                </span>
              </>
            )}
          </h2>
          
          {/* Display active filters */}
          {/* {(fromDate || toDate || selectedEquipment) && (
            <div className="active-filters">
              <span className="filter-label">Active Filters:</span>
              {fromDate && toDate && (
                <span className="filter-badge date-filter">
                  Date Range: {new Date(fromDate).toLocaleDateString()} - {new Date(toDate).toLocaleDateString()}
                </span>
              )}
              {selectedEquipment && (
                <span className="filter-badge equipment-filter">
                  Mobile Equipment: {vehicleTypes.find(v => v.VehicleID === selectedEquipment)?.ScreenName || selectedEquipment}
                </span>
              )}
            </div>
          )} */}
          
          {error && <div className="error-message">{error}</div>}
          
          {isLoading ? (
            <div className="loading-indicator">Loading violation data...</div>
          ) : violationData.length > 0 ? (
            <div className="table-scroll-container">
              <table className="violation-table">
                <thead>
                  <tr>
                    <th className="rank-col">Rank</th>
                    <th className="violation-col">Violation Type</th>
                    <th className="percentage-col">Percentage (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {violationData.map((item, index) => {
                    const percentage = parseFloat(item.ViolationPercentage);
                    
                    return (
                      <tr key={index} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                        <td className="rank-cell">{index + 1}</td>
                        <td>{item.ViolationScreenName}</td>
                        <td className="percentage-cell">{percentage.toFixed(2)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : error ? null : (
            <div className="no-data-message">No violation data available. Please adjust filters and try again.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrendAnalysisPage; 